/*
  Cart busy guard
  ---------------
  Blocks checkout while the cart is still being changed in the background.

  The theme fires a chain of requests after every add-to-cart: /cart/add.js ->
  section render -> /cart.js -> the free gift add/remove in updateFreeShipping().
  Until that chain finishes, the cart the customer can see is not the cart they
  would check out with (missing free gift, stale totals, stale discounts).

  While any cart request is in flight this script:
    - covers the whole cart drawer (or the whole cart page) with a loader,
    - swallows clicks on the checkout buttons,
    - replays the native checkout click once the cart has settled.

  Everything is fail-open: if a request hangs, the loader releases itself after
  MAX_BUSY_MS so a customer can never get locked out of checkout.
*/

(function () {
  'use strict';

  if (window.CartBusy) return;

  var SHOW_DELAY_MS = 150; // don't flash a loader for instant responses
  var SETTLE_DELAY_MS = 80; // bridge the gap between chained cart requests
  var MAX_BUSY_MS = 12000; // hard release, never trap the customer
  var KEEPALIVE_MS = 200; // the theme replaces cart markup, so re-attach

  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '.cart__summary-button',
    '.cart__dynamic-checkout',
    '.additional-checkout-buttons',
    'shopify-accelerated-checkout',
    '.shopify-payment-button'
  ].join(', ');

  var inFlight = 0;
  var visible = false;
  var showTimer = null;
  var settleTimer = null;
  var releaseTimer = null;
  var keepAliveTimer = null;
  var idleCallbacks = [];

  /* ------------------------------------------------------------------ *
   * Which requests count as "the cart is changing"
   * ------------------------------------------------------------------ */

  // "write" starts a busy window. "read" only extends one that is already
  // open, so the /cart.js the theme fires on every page load stays silent.
  function classifyRequest(url) {
    var value = String(url || '');

    if (/\/cart\/(add|change|update|clear)(\.js)?/i.test(value)) return 'write';
    // Section renders the cart UI depends on, e.g. "?sections=cart-drawer".
    if (/[?&]sections(_url)?=[^&]*cart/i.test(value)) return 'write';
    if (/\/cart\.js/i.test(value)) return 'read';

    return null;
  }

  function shouldTrack(url) {
    var kind = classifyRequest(url);

    if (!kind) return false;
    if (kind === 'read') return inFlight > 0 || !!settleTimer;

    return true;
  }

  /* ------------------------------------------------------------------ *
   * Overlay markup
   * ------------------------------------------------------------------ */

  function buildOverlay(modifier) {
    var overlay = document.createElement('div');
    overlay.className = 'cart-busy cart-busy--' + modifier;
    overlay.setAttribute('data-cart-busy', modifier);
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<div class="cart-busy__spinner lds-ring" role="status" aria-live="polite">' +
      '<span></span><span></span><span></span><span></span>' +
      '</div>';

    return overlay;
  }

  function attach(host, modifier) {
    if (!host) return null;

    var existing = host.querySelector(':scope > [data-cart-busy="' + modifier + '"]');

    if (!existing) {
      existing = buildOverlay(modifier);
      host.appendChild(existing);
    }

    return existing;
  }

  // The drawer overlay lives inside .cart-drawer__inner. That panel always has
  // a transform, so a position:fixed child is clipped to the panel and stays
  // put while the drawer scrolls.
  function drawerOverlay() {
    return attach(document.querySelector('cart-drawer .cart-drawer__inner'), 'drawer');
  }

  function pageOverlay() {
    if (!document.querySelector('cart-items.cart')) return null;

    return attach(document.body, 'page');
  }

  function isDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');

    return !!drawer && drawer.classList.contains('is-visible');
  }

  // The drawer wins when it is open; it sits on top of the page anyway.
  function activeOverlay() {
    return isDrawerOpen() ? drawerOverlay() : pageOverlay() || drawerOverlay();
  }

  function paint() {
    var active = visible ? activeOverlay() : null;

    [drawerOverlay(), pageOverlay()].forEach(function (overlay) {
      if (!overlay) return;
      overlay.classList.toggle('is-visible', overlay === active);
    });

    document.documentElement.classList.toggle('cart-is-busy', visible);
  }

  /* ------------------------------------------------------------------ *
   * Busy state
   * ------------------------------------------------------------------ */

  function startKeepAlive() {
    if (keepAliveTimer) return;
    // Cart updates blow away and rebuild the drawer markup, taking the overlay
    // with it. Re-attaching on a timer survives every one of those rewrites.
    keepAliveTimer = setInterval(paint, KEEPALIVE_MS);
  }

  function stopKeepAlive() {
    if (!keepAliveTimer) return;
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }

  function show(immediate) {
    if (visible) return;

    if (immediate) {
      clearTimeout(showTimer);
      showTimer = null;
      visible = true;
      paint();
      startKeepAlive();
      return;
    }

    if (showTimer) return;

    showTimer = setTimeout(function () {
      showTimer = null;
      visible = true;
      paint();
      startKeepAlive();
    }, SHOW_DELAY_MS);
  }

  function hide() {
    clearTimeout(showTimer);
    clearTimeout(settleTimer);
    showTimer = null;
    settleTimer = null;
    visible = false;
    stopKeepAlive();
    paint();
  }

  function runIdleCallbacks() {
    var queued = idleCallbacks;
    idleCallbacks = [];

    queued.forEach(function (callback) {
      try {
        callback();
      } catch (error) {
        console.error('cart-busy: idle callback failed', error);
      }
    });
  }

  function settle() {
    clearTimeout(settleTimer);

    settleTimer = setTimeout(function () {
      settleTimer = null;

      if (inFlight > 0) return;

      clearTimeout(releaseTimer);
      releaseTimer = null;

      var hadCallbacks = idleCallbacks.length > 0;

      if (hadCallbacks) {
        // A checkout submit is about to replay, so leave the loader up while
        // the browser navigates - but never longer than the hard release.
        releaseTimer = setTimeout(hide, MAX_BUSY_MS);
      } else {
        hide();
      }

      runIdleCallbacks();
    }, SETTLE_DELAY_MS);
  }

  function begin() {
    // A new request supersedes any settle that was counting down.
    clearTimeout(settleTimer);
    settleTimer = null;

    inFlight += 1;
    show(false);

    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(function () {
      console.warn('cart-busy: cart request took too long, releasing the loader');
      inFlight = 0;
      hide();
      runIdleCallbacks();
    }, MAX_BUSY_MS);
  }

  function end() {
    inFlight = Math.max(0, inFlight - 1);

    if (inFlight === 0) settle();
  }

  function isBusy() {
    return inFlight > 0 || !!showTimer || !!settleTimer;
  }

  function onIdle(callback) {
    if (typeof callback !== 'function') return;

    if (!isBusy()) {
      callback();
      return;
    }

    idleCallbacks.push(callback);
  }

  /* ------------------------------------------------------------------ *
   * Request tracking
   * ------------------------------------------------------------------ */

  function requestUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;

    return String(input.url || input);
  }

  function trackFetch() {
    if (typeof window.fetch !== 'function') return;

    var nativeFetch = window.fetch;

    window.fetch = function (input, init) {
      var tracked = false;

      try {
        tracked = shouldTrack(requestUrl(input));
      } catch (error) {
        tracked = false;
      }

      if (!tracked) return nativeFetch.apply(this, arguments);

      begin();

      var request;

      try {
        request = nativeFetch.apply(this, arguments);
      } catch (error) {
        end();
        throw error;
      }

      // Observe only. The original value and rejection pass straight through.
      return request.then(endOnBodyRead, function (error) {
        end();
        throw error;
      });
    };
  }

  // fetch() resolves as soon as the response headers land - the body can still
  // be downloading. Ending there opens the settle window mid-download, so we
  // wait until the caller has actually read the body out.
  function endOnBodyRead(response) {
    var finished = false;
    var bodyRequested = false;

    function finish() {
      if (finished) return;
      finished = true;
      end();
    }

    ['json', 'text', 'arrayBuffer', 'blob', 'formData'].forEach(function (method) {
      var original = response[method];

      if (typeof original !== 'function') return;

      try {
        Object.defineProperty(response, method, {
          configurable: true,
          writable: true,
          value: function () {
            bodyRequested = true;

            var result;

            try {
              result = original.apply(this, arguments);
            } catch (error) {
              finish();
              throw error;
            }

            return Promise.resolve(result).then(
              function (value) {
                finish();
                return value;
              },
              function (error) {
                finish();
                throw error;
              }
            );
          }
        });
      } catch (error) {
        // Frozen response: fall through to the tick below.
      }
    });

    // Callers that read the body do it in the microtask right after this one,
    // so by the time this macrotask runs we know whether anyone wants it.
    setTimeout(function () {
      if (!bodyRequested) finish();
    }, 0);

    return response;
  }

  function trackXhr() {
    if (typeof XMLHttpRequest === 'undefined') return;

    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__cartBusyTracked = shouldTrack(url);
      } catch (error) {
        this.__cartBusyTracked = false;
      }

      return open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (!this.__cartBusyTracked) return send.apply(this, arguments);

      var settled = false;
      var self = this;

      var done = function () {
        if (settled) return;
        settled = true;
        end();
      };

      begin();
      self.addEventListener('loadend', done);

      try {
        return send.apply(this, arguments);
      } catch (error) {
        done();
        throw error;
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Checkout guard
   * ------------------------------------------------------------------ */

  function isNativeCheckoutButton(element) {
    return (
      element &&
      element.tagName === 'BUTTON' &&
      element.getAttribute('name') === 'checkout'
    );
  }

  function submitCheckout(button) {
    var form = button.form || button.closest('form');

    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
      return;
    }

    // Older Safari: submit() skips the submitter, so carry name=checkout by hand.
    var proxy = document.createElement('input');
    proxy.type = 'hidden';
    proxy.name = 'checkout';
    proxy.value = button.value || '';
    form.appendChild(proxy);
    form.submit();
  }

  // Used when we are not waiting on a request, only on the browser leaving for
  // checkout. Without the release timer a blocked navigation (a declined form,
  // a cancelled redirect) would leave the loader up forever.
  function showForNavigation() {
    show(true);
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(hide, MAX_BUSY_MS);
  }

  function onCheckoutClick(event) {
    var trigger = event.target.closest && event.target.closest(CHECKOUT_SELECTOR);

    if (!trigger) return;

    var button = isNativeCheckoutButton(trigger) ? trigger : null;
    var form = (button && (button.form || button.closest('form'))) || null;

    // Let the browser show its own "you must agree to the terms" message.
    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) {
      return;
    }

    if (!isBusy()) {
      // Nothing pending: let the click through, but cover the cart so the
      // customer cannot fire a second checkout while the page navigates.
      if (button) showForNavigation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    show(true);

    if (!button) return; // accelerated buttons cannot be replayed safely

    onIdle(function () {
      submitCheckout(button);
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  trackFetch();
  trackXhr();

  document.addEventListener('click', onCheckoutClick, true);

  // Coming back from checkout via the back button must not restore a loader.
  window.addEventListener('pageshow', function () {
    inFlight = 0;
    idleCallbacks = [];
    clearTimeout(releaseTimer);
    clearTimeout(settleTimer);
    hide();
  });

  window.CartBusy = {
    begin: begin,
    end: end,
    isBusy: isBusy,
    onIdle: onIdle,
    show: function () {
      show(true);
    },
    hide: hide
  };
})();
